/**
 * Maps GET /api/v2/business/.../wallet/portfolio/performance response bodies
 * into chart-friendly series. Each mapper handles one API contract.
 */
import { unwrapBusinessResponse } from "./unwrapBusinessResponse";

export type BusinessPerformancePayload = {
  labels: string[];
  values: number[];
  totalDepositInZar?: number;
  totalWithdrawInZar?: number;
};

const EMPTY: BusinessPerformancePayload = { labels: [], values: [] };

function toNum(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === "string" && v.trim() !== "") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function toNumOrZero(v: unknown): number {
  return toNum(v) ?? 0;
}

/** Array of points (either primitives or { date, value, ... }). */
export function mapPerformancePointArray(
  raw: unknown[]
): BusinessPerformancePayload {
  return {
    labels: raw.map((p, i) => {
      if (p && typeof p === "object" && p !== null) {
        const o = p as Record<string, unknown>;
        if (o.date != null) {
          return String(o.date).slice(0, 10);
        }
        if (o.label != null) {
          return String(o.label);
        }
      }
      return `T${i + 1}`;
    }),
    values: raw.map((p) => {
      if (p && typeof p === "object" && p !== null) {
        const o = p as Record<string, unknown>;
        return toNumOrZero(o.value ?? o.zarValue ?? o.amount ?? o.netZar);
      }
      return toNumOrZero(p);
    }),
  };
}

/** Shape: { labels: string[], values: number[], totalDepositInZar?, totalWithdrawInZar? } */
export function tryLabelsValuesObject(
  o: Record<string, unknown>
): BusinessPerformancePayload | null {
  if (!Array.isArray(o.labels) || !Array.isArray(o.values)) {
    return null;
  }
  const valuesArr = o.values as unknown[];
  return {
    labels: (o.labels as unknown[]).map((l) => String(l)),
    values: valuesArr.map((v) => toNumOrZero(v)),
    totalDepositInZar: toNum(o.totalDepositInZar),
    totalWithdrawInZar: toNum(o.totalWithdrawInZar),
  };
}

export function tryEstimatesArray(
  o: Record<string, unknown>
): BusinessPerformancePayload | null {
  const estimates = o.estimates;
  if (!Array.isArray(estimates) || estimates.length === 0) {
    return null;
  }
  const rows = estimates as Record<string, unknown>[];
  return {
    labels: rows.map((p) =>
      p.date != null ? String(p.date) : String(p.label ?? p.asAt ?? "")
    ),
    values: rows.map((p) =>
      toNumOrZero(
        p.portfolioEstimate ?? p.value ?? p.zarValue ?? p.balance ?? p.amount
      )
    ),
    totalDepositInZar: toNum(o.totalDepositInZar),
    totalWithdrawInZar: toNum(o.totalWithdrawInZar),
  };
}

export function tryGraphDataArray(
  o: Record<string, unknown>
): BusinessPerformancePayload | null {
  const graphData = o.graphData;
  if (!Array.isArray(graphData) || graphData.length === 0) {
    return null;
  }
  const pts = graphData as Record<string, unknown>[];
  return {
    labels: pts.map((p) => {
      const x = p.x;
      if (typeof x === "number" && Number.isFinite(x)) {
        return new Date(x).toISOString();
      }
      if (x != null && String(x).trim() !== "") {
        const n = Number(x);
        if (Number.isFinite(n)) {
          return new Date(n).toISOString();
        }
      }
      return String(p.label ?? p.date ?? "");
    }),
    values: pts.map((p) => toNumOrZero(p.y ?? p.value)),
    totalDepositInZar: toNum(o.totalDepositInZar),
    totalWithdrawInZar: toNum(o.totalWithdrawInZar),
  };
}

export function tryCashFlowArray(
  o: Record<string, unknown>
): BusinessPerformancePayload | null {
  const cashFlow = o.cashFlow;
  if (!Array.isArray(cashFlow)) {
    return null;
  }
  const cf = cashFlow as Record<string, unknown>[];
  return {
    labels: cf.map((p) =>
      String(p.date ?? p.label ?? p.period ?? p.asAt ?? p.timestamp ?? "")
    ),
    values: cf.map((p) =>
      toNumOrZero(p.netZar ?? p.value ?? p.balance ?? p.total)
    ),
  };
}

export function tryDataPointsArray(
  o: Record<string, unknown>
): BusinessPerformancePayload | null {
  const dataPoints = o.dataPoints;
  if (!Array.isArray(dataPoints)) {
    return null;
  }
  const dp = dataPoints as Record<string, unknown>[];
  return {
    labels: dp.map((p) =>
      String(p.date ?? p.label ?? p.timestamp ?? p.asAt ?? "")
    ),
    values: dp.map((p) => toNumOrZero(p.value ?? p.zarValue ?? p.amount)),
  };
}

export function tryPerformanceDataArray(
  o: Record<string, unknown>
): BusinessPerformancePayload | null {
  const performanceData = o.performanceData;
  if (!Array.isArray(performanceData)) {
    return null;
  }
  const pd = performanceData as Record<string, unknown>[];
  return {
    labels: pd.map((i) => String(i.label ?? i.date ?? i.timestamp ?? "")),
    values: pd.map((i) => toNumOrZero(i.value ?? i.fiatValue)),
  };
}

export function tryDepositWithdrawTotals(
  o: Record<string, unknown>
): BusinessPerformancePayload | null {
  if (o.totalDepositInZar == null && o.totalWithdrawInZar == null) {
    return null;
  }
  return {
    labels: [],
    values: [],
    totalDepositInZar: toNum(o.totalDepositInZar),
    totalWithdrawInZar: toNum(o.totalWithdrawInZar),
  };
}

export function extractBusinessPerformancePayload(
  response: unknown
): BusinessPerformancePayload {
  const raw = unwrapBusinessResponse<Record<string, unknown> | unknown[]>(
    response as { data?: unknown }
  );
  if (raw == null) {
    return { ...EMPTY };
  }
  if (Array.isArray(raw)) {
    return mapPerformancePointArray(raw);
  }
  const o = raw as Record<string, unknown>;
  return (
    tryLabelsValuesObject(o) ??
    tryEstimatesArray(o) ??
    tryGraphDataArray(o) ??
    tryCashFlowArray(o) ??
    tryDataPointsArray(o) ??
    tryPerformanceDataArray(o) ??
    tryDepositWithdrawTotals(o) ?? { ...EMPTY }
  );
}
